import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { AppPaths } from '../appConstants';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icon';
import { IconDefinition, faAdd, faBarChart, faBars, faBell, faBusinessTime, faCalendarWeek, faChartColumn, faChevronDown, faChevronRight, faLocation, faMoneyBill, faPeopleArrows, faPieChart, faSearch, faSignOut, faThumbsDown, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useApprovers } from '../hooks/useApprovers';
import { SpinnerComponent } from './Spinner';
import { Button, ListGroup, Offcanvas, Collapse } from 'react-bootstrap';
import { useState } from 'react';

export const NavbarComponent = () => {
    const { isAuthenticated, setUpAuth, getUser, isInRole, rCodes } = useAuth();
    const { count, isLoading } = useApprovers();
    const goTo = useNavigate();
    const isAuth = isAuthenticated();
    const [show, setShow] = useState(false)
    const [adminOpen, setAdminOpen] = useState(false)
    const [dataManagementOpen, setDataManagementOpen] = useState(false)

    const handleLogout = () => {
        setUpAuth();
        goTo('/');
        window.location.reload();
    };

    const handleShow = () => setShow(false);

    const user = getUser();

    const isR20 = isInRole(rCodes?.R20);
    const isR30 = isInRole(rCodes?.R30);
    const isR40 = isInRole(rCodes?.R40);

    const isR20R30R40 = isR20 || isR30 || isR40;

    if (!isAuth) {
        return null;
    }

    const NotificationBell = () => {
        return (
            <div className="notification-bell" aria-label={`${count} pending approvals`}>
                <Icon icon={faBell} aria-hidden="true" />
                <span className="notification-count">{count}</span>
            </div>
        );
    };

    const Menu = ({ text, link, icon }: { text: string, link?: string, icon? :IconDefinition }) => {
        const isLink = !!link;
        const close = () => {
            handleShow();
            goTo(link ?? '');
        }

        return (
            <ListGroup.Item
                className={isLink ? "menu menu-item" : "menu menu-item-header"}
                action={isLink}
                onClick={isLink ? () => close() : undefined}
                role={isLink ? "button" : undefined}
                tabIndex={isLink ? 0 : undefined}
                onKeyDown={isLink ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        close();
                    }
                } : undefined}
                aria-label={isLink ? `Navigate to ${text}` : undefined}
            >
            {!!icon && <Icon icon={icon} className='mx-3' aria-hidden="true"/>}  {text}
            </ListGroup.Item>
        );
    };

    return (
        <>
            <Navbar sticky="top" expand="lg" bg="success" data-bs-theme="dark" className='d-flex flex-row justify-content-between align-items-center py-2 px-2' style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <Nav className="d-flex flex-row justify-content-start align-items-center flex-nowrap">
                    <Button
                        className="btn-link text-light bg-transparent text-decoration-none border-0 p-1 p-md-2"
                        variant="success"
                        onClick={() => setShow(true)}
                        aria-label="Open navigation menu"
                        style={{ fontSize: '1.25rem' }}
                    >
                        <Icon icon={faBars} aria-hidden="true" />
                    </Button>
                    <h3 className="fw-bold text-light mb-0 ms-1 ms-md-2" style={{ fontSize: '1.125rem' }}>MOA AMS</h3>
                </Nav>
                <Nav className="d-flex flex-row justify-content-end align-items-center gap-1 gap-md-2">
                    {isR30 && <>{isLoading ? <SpinnerComponent /> : count > 0 && <Button
                        className="btn-link bg-transparent border-0 text-light p-2"
                        onClick={() => goTo(AppPaths.getRegistrations())}
                        aria-label={`View ${count} pending approvals`}
                    >
                        {NotificationBell()}
                    </Button>}</>}
                    <Button
                        className="btn-link bg-transparent text-decoration-none border-0 text-light d-flex align-items-center justify-content-center p-2"
                        onClick={() => goTo(AppPaths.getProfile(user?.userId))}
                        aria-label="View user profile"
                    >
                        <Icon icon={faUser} aria-hidden="true"/>
                        <span className="d-none d-md-inline ms-2">{user?.fullName}</span>
                    </Button>
                    <Button
                        onClick={handleLogout}
                        className="btn-link bg-transparent text-decoration-none border-0 text-light d-flex align-items-center justify-content-center gap-2 p-2"
                        aria-label="Sign out of application"
                    >
                        <span className="d-none d-md-inline">Sign out</span>
                        <Icon icon={faSignOut} aria-hidden="true"/>
                    </Button>
                </Nav>
            </Navbar>
            <Offcanvas className="bg-success text-light" show={show} onHide={setShow} {...{
                name: 'Enable both scrolling & backdrop',
                scroll: true,
                backdrop: true,
            }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="fw-bold fs-4">MOA AMS</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="px-0 offcanvas-body">
                    <ListGroup className="mb-2 rounded-0">
                        {/* Assets Management Section */}
                        <Menu text="ASSETS MANAGEMENT" />
                        <Menu icon={faChartColumn} text="Dashboard" link={AppPaths.getDashboard()} />
                        <Menu icon={faSearch} text="Search and Filter" link={AppPaths.getSearchAndFilter()} />
                        <Menu icon={faBarChart} text="Reporting" link={AppPaths.getReporting()} />
                        {isR20R30R40 && <Menu icon={faCalendarWeek} text="Pending Reviews" link={AppPaths.getRegistrations()} />}
                        {isR20R30R40 && <Menu icon={faThumbsDown} text="Rejections" link={AppPaths.getRecentRejections()} />}
                        {isR20 && <Menu icon={faAdd} text="Register New Asset" link={AppPaths.getRegisterAsset()} />}

                        {/* Admin Portal Section */}
                        {isR40 && <>
                            <ListGroup.Item
                                className="menu menu-item-header d-flex justify-content-between align-items-center"
                                action
                                onClick={() => setAdminOpen(!adminOpen)}
                                aria-expanded={adminOpen}
                                style={{ cursor: 'pointer' }}
                            >
                                <span>ADMIN PORTAL</span>
                                <Icon icon={adminOpen ? faChevronDown : faChevronRight} aria-hidden="true"/>
                            </ListGroup.Item>
                            <Collapse in={adminOpen}>
                                <div>
                                    <ListGroup.Item
                                        className="menu menu-sub-item"
                                        action
                                        onClick={() => {
                                            handleShow();
                                            goTo(AppPaths.getUsers());
                                        }}
                                    >
                                        <Icon icon={faUsers} className='mx-3' aria-hidden="true"/> Users
                                    </ListGroup.Item>
                                    <ListGroup.Item
                                        className="menu menu-item-header menu-sub-header d-flex justify-content-between align-items-center"
                                        action
                                        onClick={() => setDataManagementOpen(!dataManagementOpen)}
                                        aria-expanded={dataManagementOpen}
                                        style={{ cursor: 'pointer', paddingLeft: '48px' }}
                                    >
                                        <span style={{ fontSize: '0.9rem' }}>Data Management</span>
                                        <Icon icon={dataManagementOpen ? faChevronDown : faChevronRight} aria-hidden="true"/>
                                    </ListGroup.Item>
                                    <Collapse in={dataManagementOpen}>
                                        <div>
                                            <ListGroup.Item
                                                className="menu menu-sub-sub-item d-flex justify-content-between align-items-center"
                                                action
                                                onClick={() => {
                                                    handleShow();
                                                    goTo(AppPaths.getCategories());
                                                }}
                                                style={{ paddingLeft: '64px' }}
                                            >
                                                <span><Icon icon={faPieChart} className='me-2' aria-hidden="true"/> Categories</span>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="d-flex align-items-center justify-content-center"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShow();
                                                        goTo(AppPaths.getCreateCategory());
                                                    }}
                                                    style={{ fontSize: '0.75rem', width: '32px', height: '32px', padding: '0' }}
                                                    aria-label="Add Category"
                                                >
                                                    <Icon icon={faAdd} className="m-0" />
                                                </Button>
                                            </ListGroup.Item>
                                            <ListGroup.Item
                                                className="menu menu-sub-sub-item d-flex justify-content-between align-items-center"
                                                action
                                                onClick={() => {
                                                    handleShow();
                                                    goTo(AppPaths.getCustodians());
                                                }}
                                                style={{ paddingLeft: '64px' }}
                                            >
                                                <span><Icon icon={faPeopleArrows} className='me-2' aria-hidden="true"/> Custodians</span>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="d-flex align-items-center justify-content-center"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShow();
                                                        goTo(AppPaths.getCreateCustodian());
                                                    }}
                                                    style={{ fontSize: '0.75rem', width: '32px', height: '32px', padding: '0' }}
                                                    aria-label="Add Custodian"
                                                >
                                                    <Icon icon={faAdd} className="m-0" />
                                                </Button>
                                            </ListGroup.Item>
                                            <ListGroup.Item
                                                className="menu menu-sub-sub-item d-flex justify-content-between align-items-center"
                                                action
                                                onClick={() => {
                                                    handleShow();
                                                    goTo(AppPaths.getLocations());
                                                }}
                                                style={{ paddingLeft: '64px' }}
                                            >
                                                <span><Icon icon={faLocation} className='me-2' aria-hidden="true"/> Locations</span>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="d-flex align-items-center justify-content-center"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShow();
                                                        goTo(AppPaths.getCreateLocation());
                                                    }}
                                                    style={{ fontSize: '0.75rem', width: '32px', height: '32px', padding: '0' }}
                                                    aria-label="Add Location"
                                                >
                                                    <Icon icon={faAdd} className="m-0" />
                                                </Button>
                                            </ListGroup.Item>
                                            <ListGroup.Item
                                                className="menu menu-sub-sub-item d-flex justify-content-between align-items-center"
                                                action
                                                onClick={() => {
                                                    handleShow();
                                                    goTo(AppPaths.getCostCenters());
                                                }}
                                                style={{ paddingLeft: '64px' }}
                                            >
                                                <span><Icon icon={faMoneyBill} className='me-2' aria-hidden="true"/> Cost Centers</span>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="d-flex align-items-center justify-content-center"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShow();
                                                        goTo(AppPaths.getCreateCostCenter());
                                                    }}
                                                    style={{ fontSize: '0.75rem', width: '32px', height: '32px', padding: '0' }}
                                                    aria-label="Add Cost Center"
                                                >
                                                    <Icon icon={faAdd} className="m-0" />
                                                </Button>
                                            </ListGroup.Item>
                                            <ListGroup.Item
                                                className="menu menu-sub-sub-item d-flex justify-content-between align-items-center"
                                                action
                                                onClick={() => {
                                                    handleShow();
                                                    goTo(AppPaths.getSuppliers());
                                                }}
                                                style={{ paddingLeft: '64px' }}
                                            >
                                                <span><Icon icon={faBusinessTime} className='me-2' aria-hidden="true"/> Suppliers</span>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="d-flex align-items-center justify-content-center"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShow();
                                                        goTo(AppPaths.getCreateSupplier());
                                                    }}
                                                    style={{ fontSize: '0.75rem', width: '32px', height: '32px', padding: '0' }}
                                                    aria-label="Add Supplier"
                                                >
                                                    <Icon icon={faAdd} className="m-0" />
                                                </Button>
                                            </ListGroup.Item>
                                        </div>
                                    </Collapse>
                                </div>
                            </Collapse>
                        </>}
                    </ListGroup>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}
